import { fileTimestamp, slugify, triggerDownload } from '../../utils/downloadFile';
import { logError } from '../../utils/logger';

export const EXPORT_WIDTH = 1920;
export const EXPORT_MIN_HEIGHT = 1080;

const FONTS_TIMEOUT_MS = 3000;
const JPEG_QUALITY = 0.95;

export type ExportFormat = 'png' | 'pdf';

/** How the capture is encoded, which is not the format the reader asked for — see `downloadNodeAs`. */
type Encoding = 'png' | 'jpeg';

/** jsPDF's own `px` unit works out at 96 points to the inch and oversizes the page by half again. */
const pointsOf = (cssPixels: number): number => (cssPixels * 72) / 96;

type Capture = { dataUrl: string; width: number; height: number };

/** Inlining Lato is the slowest part of a capture and the bytes never change, so do it once. */
let fontEmbedCSS: Promise<string> | undefined;

const embedFonts = (node: HTMLElement, getFontEmbedCSS: (node: HTMLElement) => Promise<string>): Promise<string> => {
  fontEmbedCSS ??= getFontEmbedCSS(node).catch((error) => {
    logError(error, 'Dashboard export: could not inline the fonts');
    return '';
  });
  return fontEmbedCSS;
};

const waitForFonts = (): Promise<unknown> =>
  Promise.race([
    document.fonts?.ready ?? Promise.resolve(),
    new Promise((resolve) => setTimeout(resolve, FONTS_TIMEOUT_MS)),
  ]);

/** Fixed width, measured height — a long dashboard exports taller rather than cropped. */
const captureBoard = async (node: HTMLElement, backgroundColor: string, encoding: Encoding): Promise<Capture> => {
  const { getFontEmbedCSS, toJpeg, toPng } = await import('html-to-image');
  await waitForFonts();

  const width = EXPORT_WIDTH;
  const height = Math.max(node.scrollHeight, EXPORT_MIN_HEIGHT);
  const options = {
    width,
    height,
    canvasWidth: width,
    canvasHeight: height,
    pixelRatio: 1,
    backgroundColor,
    fontEmbedCSS: await embedFonts(node, getFontEmbedCSS),
  };
  const dataUrl =
    encoding === 'png' ? await toPng(node, options) : await toJpeg(node, { ...options, quality: JPEG_QUALITY });
  return { dataUrl, width, height };
};

export const exportFilename = (dashboardName: string, format: ExportFormat, now = new Date()): string =>
  `${slugify(dashboardName) || 'dashboard'}-${fileTimestamp(now)}.${format}`;

/**
 * One page cut to the capture, so the dashboard is one sheet rather than sliced across A4.
 *
 * The PDF embeds a JPEG rather than the PNG: jsPDF passes a JPEG straight through, but re-deflates
 * a PNG in JavaScript — measured at 2.7s against 80ms for one 1920x1080 sheet.
 */
export const downloadNodeAs = async (
  format: ExportFormat,
  node: HTMLElement,
  filename: string,
  backgroundColor: string,
): Promise<void> => {
  if (format === 'png') {
    const { dataUrl } = await captureBoard(node, backgroundColor, 'png');
    triggerDownload(dataUrl, filename);
    return;
  }

  const { dataUrl, width, height } = await captureBoard(node, backgroundColor, 'jpeg');
  const { jsPDF } = await import('jspdf');
  const pageWidth = pointsOf(width);
  const pageHeight = pointsOf(height);
  const pdf = new jsPDF({
    orientation: pageWidth >= pageHeight ? 'landscape' : 'portrait',
    unit: 'pt',
    format: [pageWidth, pageHeight],
  });
  pdf.addImage(dataUrl, 'JPEG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  pdf.save(filename);
};
